import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ListChecks, Search, X, ExternalLink, Plus } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useTaskChecklistWorkspace, type ReadableChecklist } from "./task-checklist-workspace";

/** Searchable single-checklist picker for task forms. `value` null = detached. */
export function ChecklistLookupField({
  value, onChange, enabled = true,
}: {
  value: string | null | undefined;
  onChange: (id: string | null) => void;
  enabled?: boolean;
}) {
  const [search, setSearch] = useState("");
  const [picking, setPicking] = useState(false);
  const { openChecklist } = useTaskChecklistWorkspace();
  const { data: checklists = [], isLoading, isError, refetch } = useQuery<ReadableChecklist[]>({
    queryKey: ["/api/checklists"],
    enabled: enabled && (picking || !!value),
  });

  const selected = value ? checklists.find((c) => c.id === value) : undefined;
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (Array.isArray(checklists) ? checklists : []).filter((c) => !q || c.name.toLowerCase().includes(q)).slice(0, 30);
  }, [checklists, search]);

  return (
    <div className="space-y-2" data-testid="field-task-checklist">
      <Label className="flex items-center gap-1.5"><ListChecks className="h-4 w-4 text-primary" /> Checklist</Label>
      <Popover open={picking} onOpenChange={(open) => {
        setPicking(open);
        if (open) setSearch("");
      }}>
      {value ? (
        <div className="flex items-center gap-2 rounded-md border px-3 py-2 bg-muted/40">
          <ListChecks className="h-4 w-4 text-muted-foreground shrink-0" />
          <span className="flex-1 truncate text-sm" data-testid="text-selected-checklist">
            {selected?.name ?? (isLoading ? "Loading…" : "Checklist unavailable")}
          </span>
          {selected && (
            <Button type="button" variant="ghost" size="sm" className="h-7 px-2" onClick={() => openChecklist(value)} aria-label="Open checklist window" data-testid="button-open-selected-checklist">
              <ExternalLink className="h-3.5 w-3.5" />
            </Button>
          )}
          <PopoverTrigger asChild>
            <Button type="button" variant="ghost" size="sm" className="h-7 px-2" disabled={!enabled} data-testid="button-change-checklist">Change</Button>
          </PopoverTrigger>
          <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-destructive" onClick={() => onChange(null)} aria-label="Detach checklist" data-testid="button-detach-checklist">
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      ) : (
        <PopoverTrigger asChild>
          <Button type="button" variant="outline" size="sm" disabled={!enabled} data-testid="button-add-checklist">
            <Plus className="mr-1.5 h-4 w-4" /> Add checklist
          </Button>
        </PopoverTrigger>
      )}
        <PopoverContent align="start" className="z-[70] w-[min(24rem,calc(100vw-2rem))] p-0" data-testid="popover-choose-checklist" aria-label="Choose a checklist">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search checklists…"
              aria-label="Search checklists"
              className="pl-8 border-0 border-b rounded-b-none focus-visible:ring-0"
              data-testid="input-search-checklist"
            />
          </div>
          <div className="max-h-40 overflow-y-auto p-1" role="listbox" aria-label="Checklists">
            {isLoading && <p className="p-2 text-xs text-muted-foreground">Loading checklists…</p>}
            {isError && (
              <p className="p-2 text-xs text-destructive">Couldn't load checklists. <button type="button" className="underline" onClick={() => refetch()}>Retry</button></p>
            )}
            {!isLoading && !isError && filtered.length === 0 && (
              <p className="p-2 text-xs text-muted-foreground" data-testid="status-no-checklists">{search ? "No matching checklists." : "You have no checklists yet."}</p>
            )}
            {filtered.map((c) => (
              <button
                key={c.id}
                type="button"
                role="option"
                aria-selected={c.id === value}
                onClick={() => { onChange(c.id); setPicking(false); setSearch(""); }}
                className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-muted"
                data-testid={`option-checklist-${c.id}`}
              >
                <span className="flex-1 truncate">{c.name}</span>
                <span className="text-xs text-muted-foreground tabular-nums">{c.items?.filter((i) => i.checked).length ?? 0}/{c.items?.length ?? 0}</span>
                {(c as ReadableChecklist & { sharePermission?: string }).sharePermission === "view" && <span className="text-[10px] uppercase text-muted-foreground">view only</span>}
              </button>
            ))}
          </div>
            <div className="border-t p-1 text-right">
              <Button type="button" variant="ghost" size="sm" onClick={() => setPicking(false)}>Cancel</Button>
            </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
